<template>
  <a-modal
    v-if="visible"
    :title="type === 'attached' ? 'Attach Pay Code' : 'Create Pay Code'"
    :visible="visible"
    width="680px"
    :destroy-on-close="true"
    data-testid="shifts-pay-code-create-modal"
    @cancel="closePayCodeModal"
  >
    <shift-overlap-modal
      :visible.sync="visibleOverlapModal"
      :overlapping-shifts="overlappingShifts"
      :loading="false"
      data-testid="shifts-pay-code-overlap-modal"
      @reload="submit"
    />

    <a-form-model
      layout="horizontal"
      :model="form"
      :rules="rules"
      ref="payCodeForm"
      :label-col="{ span: 8 }"
      :wrapper-col="{ span: 16 }"
    >
      <a-alert
        :message="
          type === 'attached'
            ? 'To attach pay code please select pay code, associate and their shifts below.'
            : 'To create shifts with pay code select associates(-s), dates and pay code below.'
        "
        type="info"
        show-icon
      />

      <a-form-model-item :colon="false" label-align="left" label="Type">
        <a-select v-model="type" placeholder="Enter Type">
          <a-select-option value="independent">Independent</a-select-option>
          <a-select-option value="attached">Attached</a-select-option>
        </a-select>
      </a-form-model-item>

      <a-form-model-item :colon="false" label-align="left" label="Pay Code" prop="pay_code">
        <a-select v-model="payCodeProxy" placeholder="Select Pay Code">
          <a-select-option v-for="payCode in availablePayCodes" :key="payCode.id" :value="payCode.id">
            {{ payCode.name }}
          </a-select-option>
        </a-select>
      </a-form-model-item>

      <a-form-model-item
        v-if="form.pay_code"
        :colon="false"
        label-align="left"
        :label="`Value, ${form.pay_code.type.toLowerCase() === 'total' ? '$' : 'hours'}`"
      >
        <a-input-number v-model="form.pay_code.value" :min="0" :step="0.1" :precision="2" placeholder="Enter value" />
      </a-form-model-item>

      <a-form-model-item v-if="form.pay_code" :colon="false" label-align="left" label="Ready To Pay Status">
        <custom-select
          :multiple="false"
          :value.sync="form.ready_for_pay_status"
          placeholder="Status"
          :allow-clear="false"
          :options="readyForPayStatus"
          name="title"
          optionKey="key"
        />
      </a-form-model-item>

      <created-form
        v-show="type === 'independent'"
        :dates.sync="form.dates"
        :associates.sync="form.associates"
        :initial-values="initialValues"
      />

      <a-form-model-item v-if="type === 'independent'" :colon="false" label-align="left" label="Start Time" prop="time">
        <a-time-picker
          :value="timeValue"
          :allow-clear="false"
          :default-open-value="defaultTimeValue"
          format="hh:mm A"
          :use12Hours="true"
          class="w-full"
          @change="handleTimeChange"
        />
      </a-form-model-item>

      <attached-form
        v-show="type === 'attached'"
        ref="attachedRef"
        :shift_ids.sync="form.shift_ids"
        :initial-values="initialValues"
      />
    </a-form-model>

    <template slot="footer">
      <div class="flex justify-content-between">
        <a-button @click="closePayCodeModal">Cancel</a-button>
        <a-button type="primary" :loading="loading" @click="submit">{{
          type === 'attached' ? 'Attach' : 'Create'
        }}</a-button>
      </div>
    </template>
  </a-modal>
</template>

<script>
import { Alert, Button, FormModel, InputNumber, Modal, notification, Select, Table, TimePicker } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import ShiftPayCodeAttached from './shift-pay-code-attached.vue'
import ShiftPayCodeCreated from './shift-pay-code-created.vue'
import { deepCopy } from '@/utils/deep-copy'
import CustomSelect from '@/components/ui/custom-select.vue'
import { PAY_CODE_READY_FOR_PAY_STATUSES } from '@/store/modules/shifts/constants'
import ShiftOverlapModal from '../modal/shift-overlap-modal.vue'
import {
  getScheduledStartForWorkDay,
  getWorkDayStartTime,
  isScheduledStartInWorkDay,
  normalizePayCodeTime,
  timePickerValue
} from '../../utils/pay-code-time'

const { mapActions: shiftActions } = createNamespacedHelpers('shifts')

const independentForm = {
  pay_code: undefined,
  is_planned: null,
  ready_for_pay_status: 'unblocked',
  associates: [],
  dates: [],
  time: undefined
}

const independentRules = {
  pay_code: [
    {
      required: true,
      message: 'Please select pay code',
      trigger: 'change',
      type: 'object'
    }
  ],
  associates: [
    {
      required: true,
      message: 'Please select associates',
      trigger: 'change',
      type: 'array'
    }
  ],
  dates: [
    {
      required: true,
      message: 'Please select dates',
      trigger: 'change',
      type: 'array'
    }
  ],
  time: [
    {
      required: true,
      message: 'Please select start time',
      trigger: 'change'
    }
  ]
}

const attachForm = {
  pay_code: undefined,
  shift_ids: []
}

const attachRules = {
  pay_code: [
    {
      required: true,
      message: 'Please select pay code',
      trigger: 'change',
      type: 'object'
    }
  ],
  shift_ids: [
    {
      required: true,
      message: 'Please select shifts',
      trigger: 'change',
      type: 'array'
    }
  ]
}

export default {
  components: {
    'a-modal': Modal,
    'a-form-model': FormModel,
    'a-form-model-item': FormModel.Item,
    'a-input-number': InputNumber,
    'a-button': Button,
    'a-select': Select,
    'a-select-option': Select.Option,
    'a-time-picker': TimePicker,
    'a-alert': Alert,
    'a-table': Table,
    'shift-overlap-modal': ShiftOverlapModal,
    'custom-select': CustomSelect,
    'attached-form': ShiftPayCodeAttached,
    'created-form': ShiftPayCodeCreated
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    shiftId: {
      type: Number,
      default: undefined
    },
    initialValues: {
      type: Object,
      default: undefined
    },
    add: {
      type: Function
    }
  },
  data() {
    return {
      form: deepCopy(independentForm),
      rules: deepCopy(independentRules),
      type: 'independent',

      loading: false,

      stateAttachForm: deepCopy(attachForm),
      stateIndependentForm: deepCopy(independentForm),

      readyForPayStatus: Object.entries(PAY_CODE_READY_FOR_PAY_STATUSES).map(([key, value]) => ({
        key,
        ...value
      })),

      overlappingShifts: [],

      visibleOverlapModal: false
    }
  },
  computed: {
    payCodes() {
      return this.$store.state.applicationState.projectPayCodes
    },

    availablePayCodes() {
      switch (this.type) {
        case 'attached':
          return this.payCodes.filter(item => item.attached)
        case 'independent':
          return this.payCodes.filter(item => !item.attached)
        default:
          return []
      }
    },

    payCodeProxy: {
      get() {
        return this.form.pay_code ? Number(this.form.pay_code.id) : undefined
      },
      set(value) {
        this.form.pay_code = this.payCodes.find(item => item.id === value)
      }
    },

    paymentSettings() {
      return this.$store.state.applicationState.currentProject?.payment_settings
    },

    timeZone() {
      return this.$store.state.applicationState.timeZone
    },

    workDayStart() {
      return getWorkDayStartTime(this.paymentSettings)
    },

    defaultTimeValue() {
      return timePickerValue(this.workDayStart)
    },

    timeValue() {
      return timePickerValue(this.form.time, this.workDayStart)
    }
  },
  watch: {
    type: {
      handler(newVal) {
        if (newVal) {
          if (newVal === 'attached') {
            this.stateIndependentForm = { ...this.form }
          } else {
            this.stateAttachForm = { ...this.form }
          }

          this.$nextTick(() => {
            this.updateFormAndRules(newVal)
          })
        }
      },
      immediate: true
    }
  },
  methods: {
    ...shiftActions(['createPayCode', 'attachPayCode']),

    openOverlapShifts(shifts) {
      this.visibleOverlapModal = true
      this.overlappingShifts = deepCopy(shifts)
    },

    updateFormAndRules(newVal) {
      if (newVal === 'independent') {
        this.rules = deepCopy(independentRules)
        this.rules.time = [
          {
            validator: this.validateTime,
            trigger: 'change'
          }
        ]
        this.form = deepCopy(this.stateIndependentForm)
        this.ensureDefaultTime()
      } else {
        this.rules = deepCopy(attachRules)
        this.form = deepCopy(this.stateAttachForm)
      }
    },

    handleTimeChange(time) {
      this.form.time = normalizePayCodeTime(time, this.workDayStart)
      this.$nextTick(() => {
        this.$refs.payCodeForm?.validateField('time')
      })
    },

    validateTime(rule, value, callback) {
      if (!value) {
        callback(new Error('Please select start time'))
        return
      }

      const invalidDate = (this.form.dates || []).find(date => {
        const scheduledStart = getScheduledStartForWorkDay(date, value, this.workDayStart, this.timeZone)

        return !isScheduledStartInWorkDay(scheduledStart, date, this.workDayStart, this.timeZone)
      })

      if (invalidDate) {
        callback(new Error('Start time must be within the selected Work Day.'))
        return
      }

      callback()
    },

    ensureDefaultTime() {
      if (!this.form.time) {
        this.form.time = this.workDayStart
      }
    },

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    closePayCodeModal() {
      this.type = 'independent'
      this.stateAttachForm = deepCopy(attachForm)
      this.stateIndependentForm = deepCopy(independentForm)
      this.stateIndependentForm.time = this.workDayStart
      this.updateFormAndRules('independent')
      this.overlappingShifts = []
      this.$emit('update:visible', false)
    },

    // async

    async submit() {
      try {
        const valid = await new Promise(resolve => {
          this.$refs.payCodeForm.validate(resolve)
        })

        if (!valid) {
          this.showNotification('warning', 'Validation Error', 'Please fill in all required fields correctly.')

          return
        }

        this.loading = true

        this.form.pay_code.is_planned = null

        const payload = { ...this.form }

        if (this.type === 'independent') {
          payload.time = normalizePayCodeTime(payload.time, this.workDayStart)
          const data = await this.createPayCode(payload)

          if (data && data.length) {
            this.openOverlapShifts(data)

            return
          }
        } else {
          await this.attachPayCode(payload)
        }

        this.showNotification('success', 'Success', 'New pay code has been successfully saved')

        this.$emit('add')
        this.closePayCodeModal()
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.ant-row {
  display: flex;
  margin: 0 !important;
}

.ant-form {
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
}

.ant-calendar-picker {
  margin: 0;
  width: 100%;
}

.ant-tag {
  margin: 0;
}

.date-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  border: 1px solid var(--border-color-base);
  border-radius: 8px;
  background-color: var(--bg-component);
  padding: 10px;
}

.ant-input-number {
  width: 100%;
}
</style>
